import './select.less';
import './filter-select.less';
import PropTypes from 'prop-types';
import React from 'react';
import cn from 'classname';
import ClickOutside from 'react-onclickoutside';
import filter from 'lodash/filter';
import differenceBy from 'lodash/differenceBy';
import isFunction from 'lodash/isFunction';

const propTypes = {
	name: PropTypes.string.isRequired,
	placeholder: PropTypes.string.isRequired,
	// format options: [{label:'text', value:'text'}] or empty
	value: PropTypes.any,
	// callback to load options async; (inputValue: string): ?Promise
	// format options: [{label:'text', value:'text'}]
	options: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.array
	]).isRequired,
	searchable: PropTypes.bool,
	clearable: PropTypes.bool,
	multi: PropTypes.bool,
	isFilterSelect: PropTypes.bool,
	onChange: PropTypes.func,
	onClose: PropTypes.func,
	disabled: PropTypes.bool,
	shouldOpenOptions: PropTypes.bool,
	isBlockedFilter: PropTypes.bool,
	showLabel: PropTypes.bool,
	countryCode: PropTypes.bool
};

const defaultProps = {
	searchable: true,
	clearable: true,
	multi: false,
	isFilterSelect: false,
	disabled: false,
	shouldOpenOptions: false,
	isBlockedFilter: false,
	showLabel: false
};

class Select extends React.Component {

	constructor(props) {
		super(props);

		if (this.props.searchable && !isFunction(this.props.options)) {
			throw new Error('Options must be function when searchable is TRUE');
		}

		this.state = {
			isOpen: false,
			isDebouncing: false,
			isLoading: false,
			options: [],
			values: [],
			search: ''
		};
		this.finishDebouncing = this.finishDebouncing.bind(this);
	}

	componentDidMount() {

		this.setValuesUseProps(this.props);

		if (this.props.shouldOpenOptions && this.props.searchable) {
			this.open();
		}

	}

	componentWillReceiveProps(nextProps) {
		this.setValuesUseProps(nextProps);
	}

	setValuesUseProps(props) {
		let values = [];

		if (props.value && Array.isArray(props.value)) {
			values = props.multi ?  props.value : props.value.slice(0, 1);
		}

		this.setState({
			values
		});
	}

	finishDebouncing() {
		this.setState(state => ({ isDebouncing: false }));
		this.prepareOptions();
	}

	open() {
		if (this.props.disabled)
			return;
		if (!this.state.isDebouncing) {
			this.setState(state => ({ ...state, isDebouncing: true }));
			setTimeout(this.finishDebouncing, isFunction(this.props.options) ? 500 : 10);
		} else {
			this.setState(state => ({ ...state, isDebouncing: true }));
		}
	}

	close() {
		this.setState({
			isOpen:false,
			search: '',
			options: []
		});

		if (isFunction(this.props.onClose)) {
			this.props.onClose(this.props.name);
		}
	}

	handleClickOutside(e) {
		if (this.state.isOpen) {
			this.close();
		}
	}

	handleSearchChange(e) {
		const search = e.target.value;
		if (!this.state.isDebouncing) {
			this.setState({ isDebouncing: true, search });
			setTimeout(this.finishDebouncing, isFunction(this.props.options) ? 500 : 10);
		} else {
			this.setState({ search });
		}
	}

	handleOptionClick(selectedOptions) {
		this.setState(
			prev => {
				let values = [];

				if (this.props.multi) {
					values = values.concat(prev.values);
				}

				values.push(selectedOptions);

				return {
					values,
					search: ''
				};
			},
			() => {
				this.onChange();
				if (!this.props.searchable || !this.props.multi) {
					!this.props.isOpenAfterSelection && this.close();
				}

				if (this.props.searchable && this.props.multi) {
					this.refs.search.focus();
				}
			}
		);
	}

	handleRemoveValue(item) {
		this.setState(
			prev => {
				const values = filter(prev.values, prevValue => prevValue.value !== item.value);
				return {
					values
				};
			},
			() => this.onChange()
		);
	}

	handleSearchKeyDown (e) {
		if (e.key === 'Backspace' && this.state.search === '' && this.props.clearable) {
			const values = [].concat(this.state.values);
			values.pop();

			this.setState(
				prevState => ({
					isOpen: true,
					values
				}),
				() => this.onChange()
			);
		}
	}

	prepareOptions() {
		if (!isFunction(this.props.options)) {
			this.setState({
				isOpen: true,
				options: this.props.options
			});
		} else {
			this.setState(
				prevState => ({ isLoading: true }),
				() => {
					const promise = this.props.options(this.state.search);
					promise.then(data => {
						this.setState(
							prev => ({
								isOpen: true,
								isLoading: false,
								options: data.options
							}),
							() => this.props.searchable && this.refs.search.focus()
						);
					});
				}
			);
		}
	}

	omitOptionsUseValues() {
		const { options, values } = this.state;

		if (values.length === 0) return options;

		return differenceBy(options, values, 'value');
	}

	onChange() {
		if (this.props.onChange) {
			this.props.onChange(this.state.values);
		}
	}

	render() {
		const options = this.omitOptionsUseValues();
		return (
			<div className={cn({
				'ui-select': !this.props.isFilterSelect,
				'ui-filter-select': this.props.isFilterSelect,
				'is-open': this.state.isOpen,
				'is-disabled': this.props.disabled,
				'is-blocked': this.props.isBlockedFilter,
				'is-single': !this.props.multi,
				'is-searchable': this.props.searchable,
				'has-value': this.state.values.length
			})}>
				<div className="ui-select__head" onClick={this.open.bind(this)}>
					<div className={cn({
						'ui-select__head-value': true,
						'clearfix': true,
						'single': !this.props.multi
					})}>
						{ !this.props.isFilterSelect && this.state.values.map((item, i) => (
							<span className="item" key={i}>
								<span className="item__label">{ item.label }</span>
								{!this.props.disabled && this.props.clearable && (
									<span className="item__close" onClick={this.handleRemoveValue.bind(this, item)}>×</span>
								)}
							</span>
						)) }

						{ this.props.searchable && this.state.isOpen  && (
							<input
								type="text"
								ref="search"
								value={this.state.search}
								onChange={this.handleSearchChange.bind(this)}
								onKeyDown={this.handleSearchKeyDown.bind(this)}/>
						)}
					</div>
					{ this.props.showLabel === false && (
						<div className="ui-select__head-placeholder">
							{ this.state.isLoading &&  (
								<span className="ui-select__loading-spiner" />
							) }
							{ this.props.placeholder }
						</div>
					)}
				</div>
				{ this.state.isOpen && options.length === 0 && this.props.searchable && (
					<div className="ui-select__suggest no-result">
						<span className="ui-select__suggest-item">
							<span className="ui-select__suggest-item__text">No results</span>
						</span>
					</div>
				) }

				{ this.state.isOpen && options.length > 0 && (
					<div className="ui-select__suggest">
						{ options.map((item, i) => (
							<span key={i} className="ui-select__suggest-item" onClick={this.handleOptionClick.bind(this, item)}>
								<span className="ui-select__suggest-item__text">{item.label}</span>
							</span>
						)) }
					</div>
				)}

				{ this.state.isOpen && options.length > 0 && this.props.countryCode && (
					<div className="ui-select__suggest">
						{ options.map((item, i) => (
							<span key={i} className="ui-select__suggest-item" onClick={this.handleOptionClick.bind(this, item)}>
								<span className={`flag-icon flag-icon-${item.country_code}`}></span>
								<span className="ui-select__suggest-item__text">{item.label}</span>
							</span>
						)) }
					</div>
				)}
			</div>
		);
	}
}

Select.propTypes = propTypes;
Select.defaultProps = defaultProps;

export default ClickOutside(Select);


// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/shared/form-elements/select/select.js